import {useCallback, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';

import {DEBOUNCE_MS, MIN_SEARCH_LEN, NOTIFICATION_TIMEOUTS, USER_NOT_FOUND_MESSAGE} from 'src/constants';
import {useApiWithNotifications} from 'src/hooks/api-with-notification/useApiWithNotifications';
import {useDebounce} from 'src/hooks/use-debounce';
import {useAddUserForm} from 'src/modules/user/shared/use-add-user-form';
import {useUserSearch} from 'src/modules/user/shared/use-user-search';
import {inviteUser} from 'src/openapi/admin/apis/UserIdentityApi';
import {SearchDirection} from 'src/openapi/admin/models/SearchDirection';
import {UserSortField} from 'src/openapi/admin/models/UserSortField';
import {useAppSelector} from 'src/store';
import {isVendorAdminRole} from 'src/utils/role-utils';

import {useUserLookup} from '../../../hooks/use-user-lookup';
import {
    accessManagementUsersItemsSelector,
    accessManagementUsersPaginationSelector,
    accessManagementUsersSortCriteriaSelector,
    accessManagementUsersTotalCountSelector,
    setUsersData,
} from '../service/access-management-users.slice';

import {useRoles} from './use-roles';


import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';
import type {Vendor} from 'src/openapi/admin/models/Vendor';

type Params = {
    vendors: Vendor[],
    closeDialog: () => void,
};


export const useInviteUserDialog = ({vendors, closeDialog}: Params) => {
    const {reset, form, setField, setUser} = useAddUserForm();
    const {executeWithNotifications} = useApiWithNotifications();
    const reduxDispatch = useDispatch();
    const users = useAppSelector(accessManagementUsersItemsSelector);
    const totalCount = useAppSelector(accessManagementUsersTotalCountSelector);
    const pagination = useAppSelector(accessManagementUsersPaginationSelector);
    const sortCriteria = useAppSelector(accessManagementUsersSortCriteriaSelector);
    const {searchUsers, loading: searchLoading, results: suggestUsers, error: searchError} = useUserSearch();
    const {
        lookupUserById,
        loading: userLookupLoading,
        error: lookupError,
        clearError: clearLookupError,
    } = useUserLookup();

    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [userNotFound, setUserNotFound] = useState(false);
    const [alreadyInvited, setAlreadyInvited] = useState(false);
    const [emailInput, setEmailInput] = useState('');
    const [shown, setShown] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchEnabled, setSearchEnabled] = useState(true);
    const [isMinQueryShown, setIsMinQueryShown] = useState(false);

    const getApiDetailMessage = useCallback((apiError?: ApiError) => {
        const detailMessages = apiError?.detailMessages?.map(message => message.trim()).filter(Boolean) ?? [];
        return detailMessages.length ? detailMessages.join(' ') : null;
    }, []);

    const {roles: availableRoles, loading: rolesLoading} = useRoles(form.user?.userId);
    const sortUsersByCriteria = useCallback((list: typeof users) => {
        const direction = sortCriteria.sortDirection === SearchDirection.DESC ? -1 : 1;

        const getComparable = (user: typeof users[number]) => {
            switch (sortCriteria.sortField) {
                case UserSortField.FIRST_NAME:
                    return user.firstName ?? '';
                case UserSortField.LAST_NAME:
                    return user.lastName ?? '';
                case UserSortField.ROLE:
                    return user.role?.name ?? '';
                case UserSortField.VENDOR:
                    return user.vendor?.vendorName ?? '';
                case UserSortField.EMAIL:
                default:
                    return user.email ?? '';
            }
        };

        return [...list].sort((a, b) => getComparable(a).localeCompare(getComparable(b)) * direction);
    }, [sortCriteria]);

    const performSearch = useCallback((term: string) => {
        if (term.length < MIN_SEARCH_LEN) {
            setHasSearched(false);
            return;
        }
        searchUsers(term);
        setHasSearched(true);
    }, [searchUsers]);

    const debouncedSearch = useDebounce(performSearch, DEBOUNCE_MS);

    useEffect(() => {
        if (!searchEnabled) {
            setShown(false);
            setIsMinQueryShown(false);
            return;
        }

        const trimmed = emailInput.trim();

        if (!trimmed) {
            setHasSearched(false);
            setUserNotFound(false);
            setShown(false);
            setIsMinQueryShown(false);
            return;
        }
        if (trimmed.length < MIN_SEARCH_LEN) {
            setHasSearched(false);
            setUserNotFound(false);
            setShown(true);
            setIsMinQueryShown(true);
            return;
        }

        setIsMinQueryShown(false);
        setShown(true);
        debouncedSearch(trimmed);
    }, [emailInput, searchEnabled, debouncedSearch]);

    useEffect(() => {
        if (!searchLoading && hasSearched && emailInput.trim()) {
            if (suggestUsers.length === 0) {
                setUserNotFound(true);
                setError(USER_NOT_FOUND_MESSAGE);
            } else {
                setUserNotFound(false);
                if (error === USER_NOT_FOUND_MESSAGE) {
                    setError(null);
                }
            }
        }
    }, [hasSearched, searchLoading, suggestUsers, emailInput, error]);

    const vendorRequired = isVendorAdminRole(form.roleId);

    const isInviteDisabled =
       busy
       || searchLoading
       || userLookupLoading
       || rolesLoading
       || alreadyInvited
       || userNotFound
       || !form.user
       || !form.roleId
       || (vendorRequired && !form.vendorId);

    const isRoleSelectionDisabled = !form.user || searchLoading || userLookupLoading || rolesLoading || alreadyInvited;

    const handleEmailChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const email = event.currentTarget.value;
            setEmailInput(email);
            setField('email', email);
            if (!searchEnabled) {
                setSearchEnabled(true);
            }
            if (form.user) {
                setUser(null);
                setField('roleId', undefined);
                setField('vendorId', undefined);
                setAlreadyInvited(false);
            }
            setHasSearched(false);
            setUserNotFound(false);
            setError(null);
        },
        [
            setField,
            searchEnabled,
            setUser,
            setSearchEnabled,
            form.user,
            setAlreadyInvited,
            setHasSearched,
            setUserNotFound,
            setError,
        ],
    );

    const handleUserSelect = useCallback(
        async (user: UserShortInfo) => {
            setEmailInput(user.email);
            setField('email', user.email);
            setShown(false);
            setSearchEnabled(false);

            const fullUser = await lookupUserById(user.userId);

            if (fullUser) {
                setUser(fullUser);
                if (fullUser.role) {
                    setField('roleId', fullUser.role.id);
                    setField('vendorId', fullUser.vendor?.id);
                    setAlreadyInvited(true);
                }
            } else {
                setUser(null);
                setUserNotFound(true);
                setError(USER_NOT_FOUND_MESSAGE);
            }
        },
        [setUser, setField, lookupUserById],
    );

    const clearSuggest = useCallback(() => {
        setEmailInput('');
        setField('email', '');
        setUser(null);
        setError(null);
        setField('roleId', undefined);
        setField('vendorId', undefined);
        setUserNotFound(false);
        setAlreadyInvited(false);
        setShown(false);
        setSearchEnabled(true);
        clearLookupError();
    }, [setField, setUser, clearLookupError]);

    const handleRoleChange = useCallback(
        (roleId: string | number | null) => {
            const normalizedRoleId = roleId !== null && roleId !== undefined ? String(roleId) : undefined;
            setField('roleId', normalizedRoleId);
            if (!isVendorAdminRole(normalizedRoleId)) {
                setField('vendorId', undefined);
            }
        },
        [setField],
    );

    const handleVendorChange = useCallback(
        (vendorId: string | number | null) => {
            const normalizedVendorId = vendorId !== null && vendorId !== undefined ? String(vendorId) : undefined;
            setField('vendorId', normalizedVendorId);
        },
        [setField],
    );

    const handleInviteClick = useCallback(async () => {

        setBusy(true);
        try {
            if (!form.user?.userId || !form.roleId) {
                setBusy(false);
                return;
            }

            const response = await executeWithNotifications<ApiError>(
                inviteUser({
                    inviteUserInfo: {
                        userId: form.user.userId,
                        roleId: String(form.roleId),
                        vendorId: form.vendorId,
                    },
                }),
                {
                    success: 'User successfully added',
                    error: 'Unable to add user. Please try again.',
                    exception: 'Unable to add user. Please try again.',
                },
                'user-update-role',
                {
                    ...NOTIFICATION_TIMEOUTS,
                    getErrorMessage: getApiDetailMessage,
                },
            );

            if (!response?.success) {
                setBusy(false);
                return;
            }

            const selectedRole = availableRoles.find(role => role.id === String(form.roleId));
            const selectedVendor = form.vendorId ? vendors.find(vendor => vendor.id === form.vendorId) : undefined;

            if (form.user) {
                const updatedUser = {
                    ...form.user,
                    role: selectedRole ?? form.user.role,
                    vendor: selectedVendor ?? form.user.vendor,
                };

                const sortedUsers = sortUsersByCriteria([updatedUser, ...users]);
                const updatedTotalCount = totalCount + 1;
                const pagedUsers = pagination?.size ? sortedUsers.slice(0, pagination.size) : sortedUsers;

                reduxDispatch(setUsersData({
                    users: pagedUsers,
                    totalCount: updatedTotalCount,
                    loading: false,
                }));
            }

            closeDialog();
            reset();
            setEmailInput('');
            setHasSearched(false);
            setError(null);
            setUserNotFound(false);
            setAlreadyInvited(false);
            setSearchEnabled(true);
            setShown(false);
            clearLookupError();
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            setError(message);
        } finally {
            setBusy(false);
        }
    }, [
        closeDialog,
        reset,
        setEmailInput,
        clearLookupError,
        reduxDispatch,
        availableRoles,
        vendors,
        form.roleId,
        form.vendorId,
        form.user,
        users,
        totalCount,
        pagination,
        sortUsersByCriteria,
        setError,
        executeWithNotifications,
        getApiDetailMessage,
    ]);

    const handleClose = useCallback(() => {
        closeDialog();
        reset();
        setEmailInput('');
        setHasSearched(false);
        setError(null);
        setUserNotFound(false);
        setAlreadyInvited(false);
        setShown(false);
        setSearchEnabled(true);
        clearLookupError();
    }, [closeDialog, reset, clearLookupError]);

    const displayError = error || lookupError || (userNotFound ? USER_NOT_FOUND_MESSAGE : searchError);


    return {
        form,
        availableRoles,
        emailInput,
        shown,
        hasSearched,
        searchLoading,
        suggestUsers,
        userLookupLoading,
        isInviteDisabled,
        isRoleSelectionDisabled,
        displayError,
        isMinQueryShown,
        alreadyInvited,
        busy,
        handleEmailChange,
        handleUserSelect,
        clearSuggest,
        setShown,
        handleRoleChange,
        handleVendorChange,
        handleInviteClick,
        handleClose,
    };
};
